import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { TRADITIONS } from "./landing/data";
import { useSeo } from "@/lib/seo";
import { SiteHeader, SiteFooter } from "@/components/SiteHeader";
import { WaitlistModal } from "./landing/WaitlistModal";
import { openBooking } from "@/lib/booking";

const slugify = (s) => s.toLowerCase().replace(/[^a-z]+/g, "-");

export default function TraditionsPage() {
  const [waitlist, setWaitlist] = useState({ open: false, role: "Family" });
  const openWaitlist = () => openBooking({ location: "traditions" });

  useSeo({
    title: "Traditions We Honour — ಸಂಪ್ರದಾಯ | Smartha, Madhwa, Srivaishnava, Lingayat Purohiths | PurohithConnect",
    description: "PurohithConnect matches purohiths and poojaris across every tradition — Smartha, Madhwa, Srivaishnava, Veerashaiva–Lingayat, kuladevata and community rituals. Brahmin and non-Brahmin customs alike, in Bengaluru.",
    canonicalPath: "/traditions",
    jsonLd: useMemo(() => ([
      {
        "@context": "https://schema.org", "@type": "ItemList", name: "Traditions honoured by PurohithConnect",
        itemListElement: TRADITIONS.map((t, i) => ({ "@type": "ListItem", position: i + 1, name: t.name })),
      },
      {
        "@context": "https://schema.org", "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://purohithconnect.com/" },
          { "@type": "ListItem", position: 2, name: "Traditions", item: "https://purohithconnect.com/traditions" },
        ],
      },
    ]), []),
  });

  return (
    <div className="min-h-screen bg-[#FBF6EC] text-[#3B221B]" style={{ colorScheme: "light" }}>
      <SiteHeader openWaitlist={openWaitlist} />
      <main className="mx-auto max-w-7xl px-4 pb-20 md:px-8">
        <section className="pt-12 text-center">
          <p className="font-baloo text-base text-[#B23A2B]">ಸಂಪ್ರದಾಯ</p>
          <h1 className="mt-1 font-eczar text-4xl font-bold sm:text-5xl" data-testid="traditions-title">Every tradition, honoured</h1>
          <p className="mx-auto mt-3 max-w-2xl font-mukta text-base text-[#6B5044] md:text-lg">
            Rituals are not one-size-fits-all. Tell us how your family performs them, and we match a purohith or poojari who follows the same path — Brahmin and non-Brahmin traditions alike.
          </p>
        </section>

        <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {TRADITIONS.map((t) => (
            <article key={t.name} className="flex h-full items-start gap-4 rounded-[14px] border border-[#A8722A]/25 bg-[#FFFDF8] p-5 shadow-sm transition-shadow hover:shadow-md" data-testid={`tradition-card-${slugify(t.name)}`}>
              <div className="shrink-0"><t.Icon size={44} /></div>
              <div>
                <h2 className="font-eczar text-base font-semibold text-[#3B221B]">{t.name}</h2>
                <p className="font-baloo text-sm text-[#C99B55]">{t.kn}</p>
                <p className="mt-1.5 font-mukta text-sm text-[#6B5044]">{t.desc}</p>
              </div>
            </article>
          ))}
        </div>

        <div className="mt-10 rounded-[14px] border border-[#A8722A]/30 bg-[#F3EADA] px-6 py-5 text-center font-mukta text-sm text-[#6B5044] md:text-base" data-testid="traditions-banner">
          Don't see your community's custom listed? Tell us when you book — we onboard purohiths and poojaris across communities, and we'll find the right match for your family.
        </div>

        <section className="mt-12 rounded-[20px] bg-[#3B221B] p-8 text-center md:p-10">
          <h2 className="font-eczar text-2xl font-semibold text-[#FBF6EC] md:text-3xl">Your family's way, kept exactly as it is</h2>
          <p className="mx-auto mt-3 max-w-xl font-mukta text-sm text-[#FBF6EC]/75 md:text-base">
            Join the early access list and tell us your tradition — we'll match a verified purohith who performs your ceremony the way your family has always known it.
          </p>
          <button onClick={() => openWaitlist("Family")} data-testid="traditions-cta"
            className="mt-6 rounded-full bg-[#B23A2B] px-6 py-3 font-mukta font-semibold text-[#FFFDF8] hover:bg-[#9a2f22]">
            Get early access
          </button>
        </section>

        <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
          <Link to="/occasions" data-testid="traditions-to-occasions" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Browse occasions <ArrowRight size={16} /></Link>
          <Link to="/stotras" data-testid="traditions-to-stotras" className="inline-flex items-center gap-1.5 font-mukta font-semibold text-[#A8722A] hover:text-[#8f5f21]">Stotra Library <ArrowRight size={16} /></Link>
        </div>
      </main>
      <SiteFooter />
      <WaitlistModal key={`${waitlist.open}-${waitlist.role}`} open={waitlist.open} defaultRole={waitlist.role}
        onClose={() => setWaitlist((w) => ({ ...w, open: false }))} />
    </div>
  );
}
